import { useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  MULTISIG_ENROLLMENT_QUERY_KEY,
  useMultisigEnrollmentCancel,
  useMultisigEnrollmentInitiate,
} from "@/apiQueries/multisigEnrollment";
import { useUsers } from "@/apiQueries/useUsers";

import { forgetPendingEnrollment } from "@/helpers/enrollmentOutcome";

import { useLeaveConfirmation } from "@/hooks/useLeaveConfirm";
import { useRedux } from "@/hooks/useRedux";

import { useEnrollmentKeySignFlow } from "./useEnrollmentKeySignFlow";

import { ApiUser, MultisigEnrollmentSession, MultisigEnrollmentSessionStatus } from "@/types";

export type WizardStep = 0 | 1 | 2;

type UseCoSignerWizardParams = {
  orgName: string;
  currentUserId: string;
  pendingInviteeId?: string;
  activeSession?: MultisigEnrollmentSession | null;
  editMode?: boolean;
  onBack: () => void;
};

const fullName = (u: ApiUser) => `${u.first_name} ${u.last_name}`.trim() || u.email;

export const useCoSignerWizard = ({
  orgName,
  currentUserId,
  pendingInviteeId = "",
  activeSession = null,
  editMode = false,
  onBack,
}: UseCoSignerWizardParams) => {
  const { profile } = useRedux("profile");
  const queryClient = useQueryClient();
  const usersQuery = useUsers();

  const initiateMutation = useMultisigEnrollmentInitiate();
  const cancelMutation = useMultisigEnrollmentCancel();
  const keySignFlow = useEnrollmentKeySignFlow();
  const {
    keyResult,
    hasConnectedKey,
    signError,
    signRequestPending,
    disconnectWalletRef,
    signRef,
    submitKeyMutation,
    verifyKeyMutation,
    challengeXdr,
    handleKeyConnected,
    handleKeyDisconnected,
    signAndVerify,
    canProceed: canNextFromKeyStep,
  } = keySignFlow;

  const [stepIdx, setStepIdx] = useState<WizardStep>(0);
  const [secondOwnerId, setSecondOwnerId] = useState<string>("");
  const [confirmed, setConfirmed] = useState(false);
  const [inResendMode, setInResendMode] = useState<boolean>(editMode);
  const [cancelNotice, setCancelNotice] = useState<string | null>(null);
  const [isResendInvitation, setIsResendInvitation] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<"none" | "cancel">("none");

  const initialResumeState = useMemo<{
    stepIdx: WizardStep;
    confirmed: boolean;
  } | null>(() => {
    if (editMode) return null;
    const status = activeSession?.status;
    if (status === "PENDING_OWNER1_KEY") {
      return { stepIdx: 1, confirmed: false };
    }
    if (
      status === "PENDING_OWNER2_KEY" ||
      status === "READY" ||
      status === "SUBMITTING" ||
      status === "COMPLETED"
    ) {
      return { stepIdx: 2, confirmed: true };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialResumeState) return;
    setStepIdx(initialResumeState.stepIdx);
    setConfirmed(initialResumeState.confirmed);
    // Snapped once on mount — see the memo above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (secondOwnerId) return;
    if (activeSession?.owner2_user_id) {
      setSecondOwnerId(activeSession.owner2_user_id);
      return;
    }
    if (inResendMode && pendingInviteeId) {
      setSecondOwnerId(pendingInviteeId);
    }
  }, [activeSession?.owner2_user_id, inResendMode, secondOwnerId, pendingInviteeId]);

  const eligibleOwners = useMemo<ApiUser[]>(() => {
    const users = usersQuery.data ?? [];
    return users.filter((u) => u.is_active && u.roles?.includes("owner") && u.id !== currentUserId);
  }, [usersQuery.data, currentUserId]);

  const secondOwner = eligibleOwners.find((o) => o.id === secondOwnerId) ?? null;
  const inviteeName = secondOwner ? fullName(secondOwner) : "the invited co-signer";

  const hasLiveSession = inResendMode || !!activeSession;

  const shouldBlock = !confirmed && ((stepIdx === 0 && !!secondOwnerId) || stepIdx > 0);
  const { isBlocked, confirmExit, cancelExit } = useLeaveConfirmation(shouldBlock);

  const isInviteModalOpen = isBlocked && (stepIdx === 0 || (stepIdx === 1 && !hasConnectedKey));
  const isConfirmModalOpen = isBlocked && (stepIdx === 2 || (stepIdx === 1 && hasConnectedKey));

  const canNextFromChooseOwner = !!secondOwnerId;

  const goNext = async () => {
    setCancelNotice(null);
    if (stepIdx === 0 && !hasLiveSession) {
      if (!secondOwnerId) return;
      try {
        await initiateMutation.mutateAsync({ owner2UserId: secondOwnerId });
      } catch {
        return;
      }
    }
    if (stepIdx === 1) {
      const ok = await signAndVerify();
      if (!ok) return;
    }
    setStepIdx((s) => (s < 2 ? ((s + 1) as WizardStep) : s));
  };

  const goPrev = () => {
    if (stepIdx === 0) {
      onBack();
      return;
    }
    setStepIdx((s) => (s > 0 ? ((s - 1) as WizardStep) : s));
  };

  const handleConfirm = () => {
    setCancelNotice(null);
    setConfirmed(true);
  };

  const handleConfirmExit = () => {
    if (activeSession?.status === "PENDING_OWNER1_KEY") {
      // Forget the setup only once the backend has cancelled it; if the
      // cancel fails, Settings must still be able to explain its expiry.
      cancelMutation.mutate(undefined, { onSuccess: forgetPendingEnrollment });
    }
    if (hasConnectedKey) {
      void disconnectWalletRef.current?.();
    }
    confirmExit();
  };

  const executeResend = async () => {
    if (!secondOwnerId) return;
    if (activeSession?.status === "EXPIRED") {
      try {
        await initiateMutation.mutateAsync({ owner2UserId: secondOwnerId });
      } catch {
        return;
      }
    } else {
      await queryClient.invalidateQueries({ queryKey: MULTISIG_ENROLLMENT_QUERY_KEY });
    }
    setIsResendInvitation(true);
  };

  const executeCancelInvitation = async () => {
    try {
      await cancelMutation.mutateAsync();
    } catch {
      // Error banner renders the mapper output; leave local state so the user
      // can retry from the same modal.
      return;
    }
    forgetPendingEnrollment();
    setCancelNotice(`${inviteeName} is no longer invited as Owner 2.`);
    setInResendMode(false);
    setIsResendInvitation(false);
    setSecondOwnerId("");
    setModalAction("none");
  };

  const confirmedSessionStatus: MultisigEnrollmentSessionStatus =
    verifyKeyMutation.data?.status ?? activeSession?.status ?? "PENDING_OWNER2_KEY";

  const confirmedInviteeLabel = secondOwner ? fullName(secondOwner) : "the second Owner";

  const confirmedHeading =
    confirmedSessionStatus === "COMPLETED"
      ? `Co-signing is on for ${orgName}.`
      : confirmedSessionStatus === "READY" || confirmedSessionStatus === "SUBMITTING"
        ? "Finalizing co-signing."
        : "Confirmed. Waiting on Owner 2.";

  const confirmedBody =
    confirmedSessionStatus === "COMPLETED"
      ? `Every disbursement now needs two co-signers before funds go out.`
      : confirmedSessionStatus === "READY" || confirmedSessionStatus === "SUBMITTING"
        ? `${confirmedInviteeLabel} verified their co-signing key. We're recording that on the Stellar network — this usually takes a few seconds.`
        : `We've notified ${confirmedInviteeLabel} in VIA Disbursement Platform to finish their setup. Once they complete their co-signing key, co-signing turns on for ${orgName} and every disbursement will need two co-signers before funds go out.`;

  const owner1Identity = {
    name: fullName({
      first_name: profile.data.firstName,
      last_name: profile.data.lastName,
      email: profile.data.email,
    } as ApiUser),
    email: profile.data.email,
  };

  return {
    // Step navigation
    stepIdx,
    goNext,
    goPrev,
    canNextFromChooseOwner,
    canNextFromKeyStep,

    // Owner selection
    usersQuery,
    eligibleOwners,
    secondOwnerId,
    setSecondOwnerId,
    secondOwner,
    inviteeName,

    // Wallet key step
    keyResult,
    challengeXdr,
    hasConnectedKey,
    handleKeyConnected,
    handleKeyDisconnected,
    disconnectWalletRef,
    signRef,

    // Signing
    signRequestPending,
    signError,

    // Confirmation
    confirmed,
    handleConfirm,
    confirmedHeading,
    confirmedBody,

    // Session
    hasLiveSession,

    // Resend / cancel-invitation flow
    inResendMode,
    isResendInvitation,
    executeResend,
    modalAction,
    setModalAction,
    executeCancelInvitation,
    cancelNotice,

    // Exit / leave-confirmation
    isInviteModalOpen,
    isConfirmModalOpen,
    cancelExit,
    handleConfirmExit,

    // Mutation status (exposed wholesale so the view can drive its own banners)
    initiateMutation,
    submitKeyMutation,
    verifyKeyMutation,
    cancelMutation,

    // Owner 1 identity for ReviewDetails
    owner1Identity,
  };
};
